"use client";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronDown,
  ChevronRight,
  Filter,
  Search,
  SlidersHorizontal,
  Tag,
  X,
} from "@/icons";
import React, { useCallback, useMemo, useState } from "react";
import { OptimizedGlass } from "../../primitives";
import { GlassButton } from "../button/GlassButton";
import { GlassCheckbox } from "../input/GlassCheckbox";
import { GlassDateRangePicker } from "../input/GlassDateRangePicker";
import type { DateRange } from "../input/GlassDateRangePicker";
import { GlassInput } from "../input/GlassInput";
import { GlassSlider } from "../input/GlassSlider";

export type FacetScalarValue = string | number | boolean;
export type FacetRangeValue = number | number[];
export type FacetValue =
  | FacetScalarValue
  | string[]
  | FacetRangeValue
  | DateRange
  | null
  | undefined;
export type FacetValuesState = Record<string, FacetValue>;
export type SearchResultMetadata = Record<string, unknown>;

export interface Facet {
  id: string;
  label: string;
  type: "checkbox" | "range" | "select" | "daterange";
  options?: FacetOption[];
  min?: number;
  max?: number;
  step?: number;
  collapsed?: boolean;
  showCount?: boolean;
}

export interface FacetOption {
  id: string;
  label: string;
  value: string;
  count?: number;
  selected?: boolean;
  disabled?: boolean;
}

export interface SearchResult {
  id: string;
  title: string;
  description?: string;
  category?: string;
  tags?: string[];
  score?: number;
  metadata?: SearchResultMetadata;
}

export interface GlassFacetSearchProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "results"> {
  query?: string;
  onQueryChange?: (query: string) => void;
  facets?: Facet[];
  facetValues?: FacetValuesState;
  onFacetChange?: (facetId: string, value: FacetValue) => void;
  results?: SearchResult[];
  onResultSelect?: (result: SearchResult) => void;
  placeholder?: string;
  loading?: boolean;
  showFilters?: boolean;
  showResults?: boolean;
  maxResults?: number;
  className?: string;
  onSearch?: (query: string, filters: FacetValuesState) => void;
  suggestions?: string[];
  onSuggestionSelect?: (suggestion: string) => void;
  recentSearches?: string[];
  onRecentSearchSelect?: (search: string) => void;
  variant?: "default" | "compact" | "minimal";
  size?: "sm" | "md" | "lg";
  elevation?: "low" | "medium" | "high";
  "data-testid"?: string;
  "aria-label"?: string;
}

const createExpandedFacetState = (facets: Facet[]): Record<string, boolean> =>
  Object.fromEntries(facets.map((facet) => [facet.id, !facet.collapsed]));

const createClearedFacetValues = (facets: Facet[]): FacetValuesState =>
  Object.fromEntries(
    facets.map((facet) => [facet.id, facet.type === "checkbox" ? [] : null])
  );

const isActiveFacetValue = (value: FacetValue): boolean => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return value !== null && value !== undefined;
};

const toCheckboxValues = (value: FacetValue): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];

const toSliderValue = (
  value: FacetValue,
  fallback: number
): number | number[] =>
  typeof value === "number" ||
  (Array.isArray(value) &&
    value.every((item): item is number => typeof item === "number"))
    ? value
    : [fallback];

const isDateRange = (value: FacetValue): value is DateRange =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  "from" in value &&
  "to" in value;

const GlassFacetSearch = React.forwardRef<
  HTMLDivElement,
  GlassFacetSearchProps
>(
  (
    {
      query = "",
      onQueryChange = () => {},
      facets = [],
      facetValues = {},
      onFacetChange = () => {},
      results = [],
      onResultSelect,
      placeholder = "Search...",
      loading = false,
      showFilters = true,
      showResults = true,
      maxResults = 10,
      className,
      onSearch,
      suggestions = [],
      onSuggestionSelect,
      recentSearches = [],
      onRecentSearchSelect,
      variant = "default",
      size = "md",
      elevation = "medium",
      "data-testid": dataTestId,
      "aria-label": ariaLabel,
      ...props
    },
    ref
  ) => {
    const prefersReducedMotion = useReducedMotion();
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [expandedFacets, setExpandedFacets] = useState<
      Record<string, boolean>
    >(createExpandedFacetState(facets));
    const [showFacetPanel, setShowFacetPanel] = useState(
      showFilters && variant !== "default"
    );

    const handleQueryChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newQuery = e.target.value;
        onQueryChange(newQuery);
        setShowSuggestions(true);
      },
      [onQueryChange]
    );

    const handleSearch = useCallback(() => {
      onSearch?.(query, facetValues);
      setShowSuggestions(false);
    }, [query, facetValues, onSearch]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
          handleSearch();
        } else if (e.key === "Escape") {
          setShowSuggestions(false);
        }
      },
      [handleSearch]
    );

    const handleFacetToggle = useCallback((facetId: string) => {
      setExpandedFacets((prev) => ({
        ...prev,
        [facetId]: !prev[facetId],
      }));
    }, []);

    const handleFacetChange = useCallback(
      (facetId: string, value: FacetValue) => {
        onFacetChange(facetId, value);
        // Auto-search when facets change
        setTimeout(
          () => onSearch?.(query, { ...facetValues, [facetId]: value }),
          300
        );
      },
      [onFacetChange, onSearch, query, facetValues]
    );

    const clearFilters = useCallback(() => {
      const clearedValues = createClearedFacetValues(facets);
      Object.keys(clearedValues).forEach((facetId) => {
        onFacetChange(facetId, clearedValues[facetId]);
      });
      onSearch?.(query, clearedValues);
    }, [facets, onFacetChange, onSearch, query]);

    const hasActiveFilters = useMemo(() => {
      return Object.values(facetValues).some(isActiveFacetValue);
    }, [facetValues]);

    const displayedResults = useMemo(() => {
      return results.slice(0, maxResults);
    }, [results, maxResults]);

    const sizeClasses = {
      sm: "glass-text-sm",
      md: "glass-text-base",
      lg: "glass-text-lg",
    };

    const variantClasses = {
      default: "glass-p-6",
      compact: "glass-p-4",
      minimal: "glass-p-2",
    };

    const elevationClasses = {
      low: "glass-backdrop-blur-md glass-surface-dark/20 glass-border glass-border-white/10",
      medium:
        "glass-backdrop-blur-md glass-surface-dark/30 glass-border glass-border-white/12 glass-shadow-lg",
      high: "glass-backdrop-blur-md glass-surface-dark/40 glass-border glass-border-white/20 glass-shadow-2xl",
    };

    return (
      <div
        ref={ref}
        data-glass-component
        className={cn(
          "glass-radius-xl",
          "glass-max-h-full glass-min-w-0 glass-overflow-auto",
          elevationClasses[elevation],
          variantClasses[variant],
          sizeClasses[size],
          className
        )}
        data-testid={dataTestId}
        aria-label={ariaLabel || "Faceted search"}
        role="search"
        style={{ maxWidth: "100%", boxSizing: "border-box", minWidth: 0 }}
        {...props}
      >
        {/* Search Input */}
        <div className="glass-flex glass-items-center glass-gap-2 glass-mb-6 glass-pb-2">
          <GlassInput
            value={query}
            onChange={handleQueryChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="glass-min-w-0 glass-flex-1"
            leftIcon={<Search className="glass-w-4 glass-h-4" />}
          />
          <div className="glass-flex glass-shrink-0 glass-items-center glass-gap-2">
            {loading && (
              <div className="glass-w-4 glass-h-4 glass-border-2 glass-border-white/30 glass-border-t-white glass-radius-full glass-animate-spin" />
            )}
            {query && (
              <button
                onClick={(e) => onQueryChange("")}
                className="glass-text-primary-opacity-70 hover:glass-text-primary glass-transition-colors glass-focus glass-touch-target glass-contrast-guard"
                aria-label="Clear search"
              >
                <X className="glass-w-4 glass-h-4" />
              </button>
            )}
            {showFilters && (
              <button
                type="button"
                onClick={(e) => setShowFacetPanel(!showFacetPanel)}
                className={cn(
                  "glass-flex glass-h-8 glass-w-8 glass-shrink-0 glass-items-center glass-justify-center glass-radius-full glass-border glass-border-white/20 glass-bg-white/15 glass-transition glass-focus",
                  hasActiveFilters && "glass-text-primary"
                )}
                aria-label="Toggle filters"
                aria-pressed={showFacetPanel}
              >
                <SlidersHorizontal className="glass-w-4 glass-h-4" />
              </button>
            )}
          </div>

          {/* Suggestions Dropdown */}
          <AnimatePresence>
            {showSuggestions &&
              (suggestions.length > 0 || recentSearches.length > 0) && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="glass-absolute glass-left-0 glass-right-0 glass-z-10 glass-max-h-60 glass-overflow-y-auto"
                  style={{ top: "calc(100% + 8px)" }}
                >
                  <OptimizedGlass
                    elevation={"level2"}
                    className="glass-radius-lg glass-overflow-hidden glass-shadow-lg"
                  >
                    {suggestions.length > 0 && (
                      <div className="glass-p-2">
                        <div className="glass-text-xs glass-text-primary-opacity-70 glass-px-2 glass-py-1">
                          Suggestions
                        </div>
                        {suggestions.map((suggestion, index) => (
                          <button
                            key={index}
                            onClick={(e) => {
                              onSuggestionSelect?.(suggestion);
                              onQueryChange(suggestion);
                              setShowSuggestions(false);
                            }}
                            className="glass-w-full glass-text-left glass-px-2 glass-py-1 glass-text-sm glass-text-primary hover:glass-surface-subtle/10 glass-radius-md glass-transition-colors glass-focus glass-touch-target glass-contrast-guard"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}

                    {recentSearches.length > 0 && (
                      <div className="glass-p-2 glass-border-t glass-border-white/10">
                        <div className="glass-text-xs glass-text-primary-opacity-70 glass-px-2 glass-py-1">
                          Recent Searches
                        </div>
                        {recentSearches.map((search, index) => (
                          <button
                            key={index}
                            onClick={(e) => {
                              onRecentSearchSelect?.(search);
                              onQueryChange(search);
                              setShowSuggestions(false);
                            }}
                            className="glass-w-full glass-text-left glass-px-2 glass-py-1 glass-text-sm glass-text-primary hover:glass-surface-subtle/10 glass-radius-md glass-transition-colors glass-focus glass-touch-target glass-contrast-guard"
                          >
                            {search}
                          </button>
                        ))}
                      </div>
                    )}
                  </OptimizedGlass>
                </motion.div>
              )}
          </AnimatePresence>
        </div>

        {/* Facet Panel */}
        <AnimatePresence>
          {showFacetPanel && showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={
                prefersReducedMotion ? {} : { opacity: 1, height: "auto" }
              }
              exit={{ opacity: 0, height: 0 }}
              className="glass-mb-4 glass-border glass-border-white/10 glass-surface-dark/20 glass-radius-lg glass-overflow-hidden"
            >
              <div className="glass-p-3">
                <div className="glass-flex glass-items-center glass-justify-between glass-mb-3 glass-gap-2">
                  <h3 className="glass-font-semibold glass-text-primary glass-flex glass-items-center glass-gap-2">
                    <Filter className="glass-w-4 glass-h-4" />
                    Filters
                  </h3>
                  {hasActiveFilters && (
                    <GlassButton
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="glass-text-primary-opacity-70 hover:glass-text-primary"
                    >
                      Clear All
                    </GlassButton>
                  )}
                </div>

                <div className="glass-auto-gap glass-auto-gap-sm">
                  {facets.map((facet) => (
                    <FacetGroup
                      key={facet.id}
                      facet={facet}
                      value={facetValues[facet.id]}
                      onChange={(value) => handleFacetChange(facet.id, value)}
                      expanded={expandedFacets[facet.id]}
                      onToggle={() => handleFacetToggle(facet.id)}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Results */}
        {showResults && (loading || displayedResults.length > 0 || query) && (
          <div className="glass-auto-gap glass-auto-gap-sm" style={{ marginTop: 8 }}>
            <div className="glass-flex glass-items-center glass-justify-between">
              <h3 className="glass-text-base glass-font-semibold glass-text-primary">
                Results {results.length > 0 && `(${results.length})`}
              </h3>
              {results.length > maxResults && (
                <span className="glass-text-sm glass-text-primary-opacity-70">
                  Showing {maxResults} of {results.length}
                </span>
              )}
            </div>

            <AnimatePresence>
              {loading ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={prefersReducedMotion ? {} : { opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="glass-flex glass-items-center glass-justify-center glass-py-8"
                >
                  <div className="glass-w-6 glass-h-6 glass-border-2 glass-border-white/30 glass-border-t-white glass-radius-full glass-animate-spin" />
                </motion.div>
              ) : displayedResults.length > 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={prefersReducedMotion ? {} : { opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="glass-auto-gap glass-auto-gap-sm"
                >
                  {displayedResults.map((result, index) => (
                    <motion.div
                      key={result.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={prefersReducedMotion ? {} : { opacity: 1, x: 0 }}
                      transition={
                        prefersReducedMotion
                          ? { duration: 0 }
                          : { duration: 0.3 }
                      }
                      className="glass-p-3 glass-surface-dark/20 hover:glass-surface-dark/30 glass-radius-lg glass-cursor-pointer glass-transition-all glass-duration-200 glass-border glass-border-white/10 hover:glass-border-white/20"
                      onClick={(e) => onResultSelect?.(result)}
                    >
                      <div className="glass-flex glass-items-start glass-justify-between">
                        <div className="glass-flex-1">
                          <h4 className="glass-font-medium glass-text-primary">
                            {result.title}
                          </h4>
                          {result.description && (
                            <p className="glass-text-sm glass-text-primary-opacity-70 glass-mt-1">
                              {result.description}
                            </p>
                          )}
                          <div className="glass-flex glass-items-center glass-gap-2 glass-mt-2">
                            {result.category && (
                              <span className="glass-px-2 glass-py-1 glass-text-xs glass-surface-subtle/10 glass-text-primary-glass-opacity-80 glass-radius-md">
                                {result.category}
                              </span>
                            )}
                            {result.tags &&
                              result.tags.map((tag, tagIndex) => (
                                <span
                                  key={tagIndex}
                                  className="glass-flex glass-items-center glass-gap-1 glass-px-2 glass-py-1 glass-text-xs glass-surface-blue/20 glass-text-secondary glass-radius-md"
                                >
                                  <Tag className="glass-w-3 glass-h-3" />
                                  {tag}
                                </span>
                              ))}
                          </div>
                        </div>
                        {result.score && (
                          <div className="glass-text-xs glass-text-primary-glass-opacity-50">
                            {Math.round(result.score * 100)}%
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              ) : query ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={prefersReducedMotion ? {} : { opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="glass-text-center glass-py-8 glass-text-primary-opacity-70"
                >
                  No results found for "{query}"
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={prefersReducedMotion ? {} : { opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="glass-text-center glass-py-8 glass-text-primary-opacity-70"
                >
                  Start typing to search...
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    );
  }
);

GlassFacetSearch.displayName = "GlassFacetSearch";

// Facet Group Component
interface FacetGroupProps {
  facet: Facet;
  value: FacetValue;
  onChange: (value: FacetValue) => void;
  expanded: boolean;
  onToggle: () => void;
}

const FacetGroup: React.FC<FacetGroupProps> = ({
  facet,
  value,
  onChange,
  expanded,
  onToggle,
}) => {
  const prefersReducedMotion = useReducedMotion();
  return (
    <div className="glass-border glass-border-white/10 glass-surface-dark/20 glass-radius-lg glass-overflow-hidden">
      <button
        onClick={onToggle}
        className="glass-w-full glass-flex glass-items-center glass-justify-between glass-gap-2 glass-p-2 glass-text-left hover:glass-surface-subtle/5 glass-transition-colors glass-focus glass-touch-target glass-contrast-guard glass-focus glass-touch-target glass-contrast-guard"
      >
        <span
          className="glass-font-medium"
          style={{ color: "rgba(255, 255, 255, 0.95)" }}
        >
          {facet.label}
        </span>
        {expanded ? (
          <ChevronDown className="glass-w-4 glass-h-4 glass-text-primary-opacity-70" />
        ) : (
          <ChevronRight className="glass-w-4 glass-h-4 glass-text-primary-opacity-70" />
        )}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={prefersReducedMotion ? {} : { opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="glass-px-3 glass-pb-3"
          >
            <FacetContent facet={facet} value={value} onChange={onChange} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Facet Content Component
interface FacetContentProps {
  facet: Facet;
  value: FacetValue;
  onChange: (value: FacetValue) => void;
}

const FacetContent: React.FC<FacetContentProps> = ({
  facet,
  value,
  onChange,
}) => {
  switch (facet.type) {
    case "checkbox":
      return (
        <div className="glass-auto-gap glass-auto-gap-sm">
          {facet.options?.map((option) => (
            <div
              key={option.id}
              className="glass-flex glass-items-center glass-justify-between"
            >
              <GlassCheckbox
                id={option.id}
                checked={toCheckboxValues(value).includes(option.value)}
                onCheckedChange={(checked) => {
                  const currentValues = toCheckboxValues(value);
                  const newValues = checked
                    ? [...currentValues, option.value]
                    : currentValues.filter(
                        (currentValue) => currentValue !== option.value
                      );
                  onChange(newValues);
                }}
                disabled={option.disabled}
                label={option.label}
              />
              {facet.showCount && option.count != null && (
                <span className="glass-text-xs glass-text-primary-glass-opacity-50">
                  ({option.count})
                </span>
              )}
            </div>
          ))}
        </div>
      );

    case "range":
      return (
        <div className="glass-auto-gap glass-auto-gap-md">
          <GlassSlider
            min={facet.min || 0}
            max={facet.max || 100}
            step={facet.step || 1}
            value={toSliderValue(value, facet.min || 0)}
            onValueChange={onChange}
            className="glass-w-full"
          />
          <div className="glass-flex glass-justify-between glass-text-sm glass-text-primary-opacity-70">
            <span>{facet.min || 0}</span>
            <span>{facet.max || 100}</span>
          </div>
        </div>
      );

    case "select":
      return (
        <div className="glass-auto-gap glass-auto-gap-sm">
          {facet.options?.map((option) => (
            <GlassCheckbox
              key={option.id}
              id={option.id}
              checked={value === option.value}
              onCheckedChange={(checked) => {
                onChange(checked ? option.value : null);
              }}
              disabled={option.disabled}
              label={option.label}
            />
          ))}
        </div>
      );

    case "daterange":
      return (
        <GlassDateRangePicker
          value={isDateRange(value) ? value : undefined}
          onChange={onChange}
          placeholder="Select date range"
        />
      );

    default:
      return null;
  }
};

export { GlassFacetSearch };
